import type { ReactElement } from 'react';
import { EmployerTimeBucketIcon } from '../task/employer/EmployerTimeBucketIcon';
import { resolveGreetingTimeBucket } from '../../lib/greetingTimeOfDay';

/** Temporarily hide greeting icon on helper home & employer dashboard; set true to show again. */
export const GREETING_TIME_OF_DAY_ICON_VISIBLE = false;

type GreetingTimeOfDayIconProps = {
  hour: number;
  className?: string;
};

/** 5:00–8:59 greeting icon (sunrise); task list morning bucket keeps this look via EmployerTimeBucketIcon. */
export const GreetingEarlyMorningIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <defs>
      <linearGradient
        id="greetingTimeOfDay-sunriseSky"
        x1="15"
        y1="0"
        x2="15"
        y2="30"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0%" stopColor="#F97316" />
        <stop offset="60%" stopColor="#FDBA74" />
        <stop offset="100%" stopColor="#FEF08A" />
      </linearGradient>
    </defs>
    <rect width="30" height="30" rx="6" fill="#FFEDD5" />
    <rect x="0" y="0" width="30" height="30" fill="url(#greetingTimeOfDay-sunriseSky)" />
    <circle cx="21" cy="18" r="5" fill="#FEF08C" stroke="#A16207" strokeWidth="0.3" />
    <g stroke="#FEF08C" strokeWidth="1.8" strokeLinecap="round">
      <line x1="21" y1="11" x2="21" y2="9" />
      <line x1="14" y1="18" x2="12" y2="18" />
      <line x1="16" y1="13" x2="14" y2="11" />
      <line x1="26" y1="13" x2="28" y2="11" />
      <line x1="21" y1="25" x2="21" y2="27" />
    </g>
    <path d="M0 30 L0 23 L8 19 L16 22 L24 17 L30 21 L30 30 Z" fill="#14532D" />
  </svg>
);

/** 9:00–11:59 greeting icon only. */
export const GreetingLateMorningIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <defs>
      <linearGradient
        id="greetingTimeOfDay-middaySky"
        x1="15"
        y1="0"
        x2="15"
        y2="30"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0%" stopColor="#38BDF8" />
        <stop offset="70%" stopColor="#7DD3FC" />
        <stop offset="100%" stopColor="#E0F2FE" />
      </linearGradient>
    </defs>
    <rect width="30" height="30" rx="6" fill="#E0F2FE" />
    <rect x="0" y="0" width="30" height="30" fill="url(#greetingTimeOfDay-middaySky)" />
    <circle cx="15" cy="11" r="5" fill="#FEF08C" stroke="#A16207" strokeWidth="0.3" />
    <g stroke="#FEF08C" strokeWidth="1.8" strokeLinecap="round">
      <line x1="15" y1="4" x2="15" y2="2" />
      <line x1="15" y1="18" x2="15" y2="20" />
      <line x1="7" y1="11" x2="5" y2="11" />
      <line x1="23" y1="11" x2="25" y2="11" />
      <line x1="9.5" y1="5.5" x2="8" y2="4" />
      <line x1="20.5" y1="5.5" x2="22" y2="4" />
      <line x1="9.5" y1="16.5" x2="8" y2="18" />
      <line x1="20.5" y1="16.5" x2="22" y2="18" />
    </g>
    <path d="M0 30 L0 23 L8 19 L16 22 L24 17 L30 21 L30 30 Z" fill="#16A34A" />
  </svg>
);

const resolveGreetingIcon = (hour: number): (() => ReactElement) | null => {
  if (hour >= 5 && hour <= 8) {
    return GreetingEarlyMorningIcon;
  }
  if (hour >= 9 && hour <= 11) {
    return GreetingLateMorningIcon;
  }
  return null;
};

export const GreetingTimeOfDayIcon = ({ hour, className = 'h-[30px] w-[30px] shrink-0' }: GreetingTimeOfDayIconProps) => {
  const GreetingIcon = resolveGreetingIcon(hour);
  const iconContent = GreetingIcon ? (
    <span className={className}>
      <GreetingIcon />
    </span>
  ) : (
    <EmployerTimeBucketIcon bucket={resolveGreetingTimeBucket(hour)} className={className} />
  );

  if (!GREETING_TIME_OF_DAY_ICON_VISIBLE) {
    return (
      <span className="hidden" aria-hidden>
        {iconContent}
      </span>
    );
  }

  return iconContent;
};
